import { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Message } from "shared/types/chatBoard";
import { useNavigate, useParams } from "react-router-dom";
import { routeConstant } from "shared/routes/routeConstant";
import { mapReplyToMessage } from "shared/utils/chatBoardUtils";
import { Icons } from "assets";
import classNames from "classnames";
import styles from "./style.module.scss";
import useWindowDimensions from "shared/hooks/useWindowDimensions";
import CustomTextEditor from "shared/components/common/customTextEditor";
import Spinner from "shared/components/common/spinner";
import ChatBoardCommentSkeleton from "shared/components/common/chatBoardCommentSkeleton";
import MessageItem from "../../messageItem";
import useScrollToMessage from "shared/hooks/useScrollToMessage";
import useChatBoardComments from "shared/hooks/useChatBoardComments";
import useChatBoardReplies from "shared/hooks/useChatBoardReplies";
import useChatBoardForms from "shared/hooks/useChatBoardForms";
import useChatBoardSocket from "shared/hooks/useChatBoardSocket";
import NoContentCard from "shared/components/common/noContentCard";

interface ChatBoardProps {
  messages?: Message[];
  navigateToThread?: (messageId: string, message: Message) => void;
  projectId?: string;
  chatBoardId?: string | number;
  mobileFilterDropdown?: React.ReactNode;
  isReadOnly?: boolean;
}

const ChatBoard = ({
  messages: propMessages = [],
  navigateToThread,
  projectId,
  chatBoardId,
  mobileFilterDropdown,
  isReadOnly = false,
}: ChatBoardProps) => {
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const { width } = useWindowDimensions();
  const isMobile = width < 640;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const auth = useSelector((state: any) => state.root.auth);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  const {
    messages,
    setMessages,
    loading,
    commentsLoading,
    hasMoreComments,
    loadMoreComments,
  } = useChatBoardComments({
    chatBoardId,
    propMessages,
    messagesContainerRef,
  });

  const { replies, setReplies, loadingReplies, fetchReplies } =
    useChatBoardReplies({
      setMessages,
    });

  const { socketRef } = useChatBoardSocket({
    chatBoardId,
    userId: auth?.user?.user_id,
    setMessages,
    setReplies,
    setSelectedMessage,
    messagesContainerRef,
  });

  const {
    message,
    setMessage,
    replyMessage,
    setReplyMessage,
    selectedFiles,
    replyFiles,
    isSubmitting,
    isSubmittingReply,
    handleFileSelect,
    handleRemoveFile,
    handleReplyFileSelect,
    handleRemoveReplyFile,
    handleSubmitComment,
    handleSubmitReply,
    handleCloseReplyPanel,
  } = useChatBoardForms({
    chatBoardId,
    auth,
    socketRef,
    messagesContainerRef,
    setMessages,
    selectedMessage,
    setSelectedMessage,
    setReplies,
  });

  useScrollToMessage({
    messages,
    containerRef: messagesContainerRef,
    loading,
    commentsLoading,
  });

  const getFileIcon = (type: string) => {
    if (type === "image") {
      return <Icons.TabImage className={styles.fileIcon} />;
    }
    return <Icons.TabOverview className={styles.fileIcon} />;
  };

  const handleReplyClick = (msg: Message) => {
    if (isMobile) {
      if (navigateToThread) {
        navigateToThread(id || projectId || "", msg);
      } else {
        const pathname = window.location.pathname;
        let threadPath = routeConstant.designer.projectDetail.thread.path;

        if (pathname.includes("/client/")) {
          threadPath = routeConstant.client.projectDetail.thread.path;
        } else if (pathname.includes("/collaborator/")) {
          threadPath = routeConstant.collaborator.projectDetail.thread.path;
        } else if (pathname.includes("/builder/")) {
          threadPath = routeConstant.builder.projectDetail.thread.path;
        }

        navigate(threadPath.replace(":id", id || projectId || ""), {
          state: {
            selectedMessage: msg,
            fromChatBoard: true,
            messageId: msg.id,
            isReadOnly,
          },
        });
      }
    } else {
      setSelectedMessage(msg);
    }

    if (
      (msg.repliesCount ?? 0) > 0 &&
      !replies[msg.id] &&
      !loadingReplies[msg.id]
    ) {
      fetchReplies(msg.id);
    }
  };

  return (
    <div
      className={classNames(
        styles.chatBoardWrapper,
        "h-full flex flex-col gap-4",
      )}
    >
      {mobileFilterDropdown}
      <div className={classNames(styles.chatBoardContainer, "h-full")}>
        <div className={classNames(styles.leftPanel, "h-full")}>
          <div
            ref={messagesContainerRef}
            className={classNames(styles.messagesContainer, "h-full")}
            style={{
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            }}
            onScroll={(e) => {
              const target = e.currentTarget;
              const top = target.scrollTop <= 100;
              if (top && hasMoreComments && !commentsLoading && chatBoardId) {
                loadMoreComments();
              }
            }}
          >
            {loading && messages.length === 0 ? (
              <ChatBoardCommentSkeleton count={3} />
            ) : messages.length === 0 ? (
              <div className="flex items-center justify-center h-[600px]">
                <NoContentCard
                  label1="No messages yet"
                  label2=""
                  Icon={Icons.NoMessagesIcon}
                />
              </div>
            ) : (
              <>
                {commentsLoading && messages.length > 0 && (
                  <div className="flex items-center justify-center py-4">
                    <Spinner />
                  </div>
                )}
                <div></div>
                {messages.map((msg) => (
                  <div key={msg.id} id={`message-${msg.id}`}>
                    <MessageItem
                      message={msg}
                      onReplyClick={handleReplyClick}
                      variant="default"
                      replies={replies[msg.id]}
                      getFileIcon={getFileIcon}
                    />
                  </div>
                ))}
              </>
            )}
          </div>

          <div
            className={classNames(
              styles.messageInputContainer,
              "sm:static fixed bottom-0 left-0 right-0",
            )}
          >
            <CustomTextEditor
              value={message}
              onChange={setMessage}
              customContainerStyle={styles.commentsEditor}
              customToolbarStyle={styles.commentsToolbar}
              placeholder={isReadOnly ? "This project is completed. Chat is read-only." : "Type your message here..."}
              onFileSelect={handleFileSelect}
              onSend={handleSubmitComment}
              selectedFiles={selectedFiles}
              onRemoveFile={handleRemoveFile}
              isSubmitting={isSubmitting}
              showFileUpload={true}
              showSendButton={true}
              fileInputId={`file-input-${chatBoardId || id || "default"}`}
              disabled={isReadOnly}
            />
          </div>
        </div>

        {selectedMessage && !isMobile && (
          <div className={styles.replyPanel}>
            <div className={styles.replyPanelHeader}>
              <button
                className={styles.cancelButton}
                onClick={handleCloseReplyPanel}
              >
                <Icons.Cross className={styles.cancelIcon} />
                Cancel
              </button>
            </div>
            <div className={styles.replyPanelContent}>
              <div className={styles.selectedMessageContainer}>
                <MessageItem
                  message={selectedMessage}
                  onReplyClick={handleReplyClick}
                  variant="replyPanel"
                  replies={replies[selectedMessage.id]}
                  getFileIcon={getFileIcon}
                />
              </div>

              {(selectedMessage.repliesCount ?? 0) > 0 && (
                <div className={styles.repliesContainer}>
                  {loadingReplies[selectedMessage.id] ? (
                    <div
                      className={classNames(
                        styles.repliesList,
                        "flex flex-col gap-4",
                      )}
                    >
                      <ChatBoardCommentSkeleton
                        count={3}
                        className={styles.replySkeleton}
                      />
                    </div>
                  ) : replies[selectedMessage.id] &&
                    replies[selectedMessage.id].length > 0 ? (
                    <div
                      className={classNames(
                        styles.repliesList,
                        "flex flex-col gap-4",
                      )}
                    >
                      {replies[selectedMessage.id].map(
                        (reply: any, index: number) => {
                          const replyMessage = mapReplyToMessage(reply);
                          return (
                            <div key={reply.reply_id || reply.id || index}>
                              <MessageItem
                                message={replyMessage}
                                variant="default"
                                showReplyButton={false}
                                getFileIcon={getFileIcon}
                                removeBackgroundAndPadding={true}
                              />
                            </div>
                          );
                        },
                      )}
                    </div>
                  ) : null}
                </div>
              )}

              <div className={classNames(styles.replyInputContainer)}>
                <CustomTextEditor
                  value={replyMessage}
                  onChange={setReplyMessage}
                  customContainerStyle={styles.replyInputEditor}
                  customToolbarStyle={styles.replyInputToolbar}
                  placeholder={isReadOnly ? "This project is completed. Chat is read-only." : "Type your reply here..."}
                  onFileSelect={handleReplyFileSelect}
                  onSend={handleSubmitReply}
                  selectedFiles={replyFiles}
                  onRemoveFile={handleRemoveReplyFile}
                  isSubmitting={isSubmittingReply}
                  showFileUpload={true}
                  showSendButton={true}
                  fileInputId="reply-file-input"
                  disabled={isReadOnly}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatBoard;
